import React, { useState } from 'react';
import {
  Box,
  Flex,
  Heading,
  Button,
  Spinner,
  Text,
  useDisclosure,
  IconButton,
} from '@chakra-ui/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Funnel } from 'phosphor-react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { DropResult } from '@hello-pangea/dnd';
import {
  IKanbanBoard,
  IKanbanColumn,
  IKanbanCard,
  IKanbanCampoDefinicao,
  IKanbanResponse,
  IKanbanUserOption,
} from '../../../hooks/useKanban';
import { useClienteKanban } from '../../../hooks/useClienteKanban';
import { calculateRankBetween } from '../../../utils/lexoRank';
import { KanbanBoard } from '../../../components/kanban/KanbanBoard';
import { KanbanCardModal } from '../../../components/kanban/KanbanCardModal';
import { KanbanFieldFilters } from '../../../components/kanban/KanbanFieldFilters';
import SecaoClienteLayout from '../layout';
import { api } from '../../../services/api';

export default function KanbanClienteBoard() {
  const { eventoId } = useParams<{ eventoId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { getBoard, moveCard } = useClienteKanban();

  const token = localStorage.getItem('cliente_access_token');
  const numericEventoId = eventoId ? parseInt(eventoId, 10) : undefined;

  // Modais
  const {
    isOpen: isCardModalOpen,
    onOpen: onCardModalOpen,
    onClose: onCardModalClose,
  } = useDisclosure();

  // Estado
  const [selectedCard, setSelectedCard] = useState<IKanbanCard | null>(null);
  const [optimisticColumns, setOptimisticColumns] = useState<IKanbanColumn[] | null>(null);
  const [filtroCampos, setFiltroCampos] = useState<Record<string, unknown>>({});
  const [showFilters, setShowFilters] = useState(false);

  // Query do evento (para nome)
  const { data: eventoData } = useQuery({
    queryKey: ['cliente-evento-detalhe', numericEventoId],
    queryFn: async () => {
      const res = await api.get(`/api/v1/secao-cliente/eventos/${numericEventoId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data?.data;
    },
    enabled: !!numericEventoId,
  });

  // Query do board
  const { data, isLoading, error } = useQuery<IKanbanResponse<IKanbanBoard>>({
    queryKey: ['cliente-kanban-board', numericEventoId],
    queryFn: () => getBoard(numericEventoId!),
    enabled: !!numericEventoId,
  });

  const columns = data?.data?.columns || [];

  // Extrair usuarios unicos dos cards
  const projectUsers = React.useMemo(() => {
    const userMap = new Map<number, IKanbanUserOption>();
    columns.forEach(col =>
      (col.cards || []).forEach(card =>
        (card.usuarios || []).forEach(u => {
          if (!userMap.has(u.id)) {
            userMap.set(u.id, { id: u.id, nome: u.nome || '', email: '' });
          }
        }),
      ),
    );
    return Array.from(userMap.values()).sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
  }, [columns]);

  // Extrair campos dos cards
  const campos = React.useMemo(() => {
    const campoMap = new Map<number, IKanbanCampoDefinicao>();
    columns.forEach(col =>
      (col.cards || []).forEach(card =>
        (card.campo_valores || []).forEach(cv => {
          if (!campoMap.has(cv.campo_id)) {
            campoMap.set(cv.campo_id, {
              id: cv.campo_id,
              projeto_id: 0,
              nome: cv.campo_nome,
              tipo: cv.tipo as any,
              opcoes: null,
              obrigatorio: false,
              ordem: campoMap.size,
            });
          }
        }),
      ),
    );
    return Array.from(campoMap.values());
  }, [columns]);

  // Filtros locais
  const applyLocalFilters = (cols: typeof columns) => {
    const responsavelId = filtroCampos.responsavel ? Number(filtroCampos.responsavel) : null;
    const expiraDe = filtroCampos.expiraDe ? String(filtroCampos.expiraDe) : null;
    const expiraAte = filtroCampos.expiraAte ? String(filtroCampos.expiraAte) : null;

    if (!responsavelId && !expiraDe && !expiraAte) return cols;

    return cols.map(col => ({
      ...col,
      cards: (col.cards || []).filter(card => {
        if (responsavelId && !card.usuarios.some(u => u.id === responsavelId)) return false;
        if (expiraDe || expiraAte) {
          if (!card.expira_em) return false;
          const exp = card.expira_em.split('T')[0];
          if (expiraDe && exp < expiraDe) return false;
          if (expiraAte && exp > expiraAte) return false;
        }
        return true;
      }),
    }));
  };

  const filteredColumns = applyLocalFilters(columns);
  const displayColumns = optimisticColumns || filteredColumns;

  const activeFilterCount = Object.values(filtroCampos).filter(
    v => v !== null && v !== undefined && v !== '',
  ).length;

  // Move card mutation
  const moveCardMutation = useMutation({
    mutationFn: moveCard,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cliente-kanban-board', numericEventoId] });
    },
    onError: () => {
      toast.error('Erro ao mover card');
      queryClient.invalidateQueries({ queryKey: ['cliente-kanban-board', numericEventoId] });
    },
  });

  // Handlers
  const handleCardClick = (card: IKanbanCard) => {
    setSelectedCard(card);
    onCardModalOpen();
  };

  const clearOptimisticWithDelay = () => {
    setTimeout(() => setOptimisticColumns(null), 800);
  };

  const handleDragEnd = (result: DropResult) => {
    const { source, destination, type, draggableId } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index)
      return;

    // Cliente não pode mover colunas
    if (type === 'column') return;

    if (type === 'card') {
      const cardId = parseInt(draggableId.replace('card-', ''), 10);
      const sourceColumnId = parseInt(source.droppableId.replace('column-', ''), 10);
      const destColumnId = parseInt(destination.droppableId.replace('column-', ''), 10);

      const sourceColumn = columns.find(c => c.id === sourceColumnId);
      const destColumn = columns.find(c => c.id === destColumnId);
      if (!sourceColumn || !destColumn) return;

      let prevRank: string | null = null;
      let nextRank: string | null = null;

      if (sourceColumnId === destColumnId) {
        const cardsWithoutMoved = (sourceColumn.cards || []).filter(c => c.id !== cardId);
        const adjustedIndex =
          source.index < destination.index ? destination.index - 1 : destination.index;

        if (adjustedIndex === 0) {
          nextRank = cardsWithoutMoved[0]?.rank || null;
        } else if (adjustedIndex >= cardsWithoutMoved.length) {
          prevRank = cardsWithoutMoved[cardsWithoutMoved.length - 1]?.rank || null;
        } else {
          prevRank = cardsWithoutMoved[adjustedIndex - 1]?.rank || null;
          nextRank = cardsWithoutMoved[adjustedIndex]?.rank || null;
        }
      } else {
        const destCards = destColumn.cards || [];
        if (destination.index === 0) {
          nextRank = destCards[0]?.rank || null;
        } else if (destination.index >= destCards.length) {
          prevRank = destCards[destCards.length - 1]?.rank || null;
        } else {
          prevRank = destCards[destination.index - 1]?.rank || null;
          nextRank = destCards[destination.index]?.rank || null;
        }
      }

      const newRank = calculateRankBetween(prevRank, nextRank);
      const card = sourceColumn.cards?.find(c => c.id === cardId);

      if (card) {
        const updatedCard = { ...card, rank: newRank };
        const spliceIndex =
          sourceColumnId === destColumnId && source.index < destination.index
            ? destination.index - 1
            : destination.index;

        const newColumns = columns.map(col => {
          if (col.id === sourceColumnId && sourceColumnId !== destColumnId) {
            return { ...col, cards: (col.cards || []).filter(c => c.id !== cardId) };
          }
          if (col.id === destColumnId) {
            const newCards = (col.cards || []).filter(c => c.id !== cardId);
            newCards.splice(spliceIndex, 0, updatedCard);
            return { ...col, cards: newCards };
          }
          return col;
        });
        setOptimisticColumns(newColumns);
      }

      moveCardMutation.mutate(
        {
          eventoId: numericEventoId!,
          cardId,
          column_id: destColumnId,
          prev_rank: prevRank,
          next_rank: nextRank,
        },
        { onSettled: clearOptimisticWithDelay },
      );
    }
  };

  const handleBack = () => navigate(`/secao-cliente/eventos/${eventoId}`);

  if (isLoading) {
    return (
      <SecaoClienteLayout>
        <Flex justify="center" align="center" minH="400px">
          <Spinner size="xl" color="blue.500" />
        </Flex>
      </SecaoClienteLayout>
    );
  }

  if (error) {
    return (
      <SecaoClienteLayout>
        <Flex justify="center" align="center" minH="400px" flexDirection="column" gap={4}>
          <Text color="red.500">Erro ao carregar o kanban</Text>
          <Button onClick={handleBack} variant="outline">
            Voltar
          </Button>
        </Flex>
      </SecaoClienteLayout>
    );
  }

  return (
    <SecaoClienteLayout>
      <Box bg="white" minH="100vh">
        {/* Header */}
        <Box
          px={{ base: 4, md: 8 }}
          pt={5}
          pb={4}
          borderBottom="1px solid"
          borderColor="gray.100"
          bg="white"
          position="sticky"
          top={0}
          zIndex={10}
        >
          <Flex justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={3}>
            <Flex alignItems="center" gap={3}>
              <IconButton
                aria-label="Voltar"
                icon={<ArrowLeft size={18} weight="bold" />}
                variant="ghost"
                borderRadius="full"
                size="sm"
                onClick={handleBack}
                color="gray.400"
                _hover={{ bg: 'gray.100', color: 'gray.600' }}
              />
              <Box>
                <Text
                  fontSize="10px"
                  color="gray.400"
                  fontWeight="600"
                  textTransform="uppercase"
                  letterSpacing="0.08em"
                >
                  Kanban do Evento
                </Text>
                <Heading
                  fontSize={{ base: 'lg', md: 'xl' }}
                  fontWeight="700"
                  color="gray.900"
                  letterSpacing="-0.02em"
                >
                  {eventoData?.nome || 'Carregando...'}
                </Heading>
              </Box>
            </Flex>

            <Flex alignItems="center" gap={3}>
              <Button
                size="sm"
                variant="ghost"
                leftIcon={<Funnel size={15} weight={activeFilterCount > 0 ? 'fill' : 'bold'} />}
                borderRadius="full"
                color={activeFilterCount > 0 ? 'blue.600' : 'gray.500'}
                bg={activeFilterCount > 0 ? 'blue.50' : 'transparent'}
                _hover={{ bg: activeFilterCount > 0 ? 'blue.100' : 'gray.100' }}
                onClick={() => setShowFilters(!showFilters)}
                fontSize="13px"
                fontWeight="500"
              >
                Filtros
                {activeFilterCount > 0 && (
                  <Flex
                    ml={1.5}
                    bg="blue.500"
                    color="white"
                    w="18px"
                    h="18px"
                    borderRadius="full"
                    fontSize="10px"
                    fontWeight="700"
                    alignItems="center"
                    justifyContent="center"
                  >
                    {activeFilterCount}
                  </Flex>
                )}
              </Button>
            </Flex>
          </Flex>

          {showFilters && (
            <KanbanFieldFilters
              campos={campos}
              filtros={filtroCampos}
              usuarios={projectUsers}
              onChange={setFiltroCampos}
            />
          )}
        </Box>

        {/* Board */}
        <Box px={{ base: 4, md: 6 }} py={5} bg="gray.50" minH="calc(100vh - 100px)">
          {displayColumns.length === 0 ? (
            <Flex justify="center" align="center" minH="50vh">
              <Text color="gray.400" fontSize="sm">
                Nenhuma coluna no kanban deste evento
              </Text>
            </Flex>
          ) : (
            <KanbanBoard
              columns={displayColumns}
              canManage={false}
              canEditProtected={false}
              onCardClick={handleCardClick}
              onAddCardClick={() => {}}
              onEditColumn={() => {}}
              onArchiveColumn={() => {}}
              onDragEnd={handleDragEnd}
              onAddColumn={() => {}}
            />
          )}
        </Box>
      </Box>

      <KanbanCardModal
        isOpen={isCardModalOpen}
        onClose={onCardModalClose}
        card={selectedCard}
        campos={campos}
        onSave={() => {}}
        onArchive={() => {}}
      />
    </SecaoClienteLayout>
  );
}
